import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle, Lock } from "lucide-react";
import { emptyLine, emptyPayout, validateExpense, appendExpense, PayoutPicker, ExpenseLines } from "../features/expenses/ExpenseFields";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ExpenseForm() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [categories, setCategories] = useState([]);
  const [submitter, setSubmitter] = useState({ name: "", email: "" });
  const [payout, setPayout] = useState(emptyPayout());
  const [lines, setLines] = useState([emptyLine()]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    axios.get(`${API}/events/${eventId}/public`).then(r => setEvent(r.data)).catch(() => setEvent({ expenses_open: false }));
    axios.get(`${API}/categories/public`).then(r => setCategories(r.data)).catch(() => {});
  }, [eventId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!submitter.name || !submitter.email) return toast.error("Please fill in your name and email");
    const err = validateExpense(payout, lines);
    if (err) return toast.error(err);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", submitter.name);
      fd.append("email", submitter.email);
      appendExpense(fd, payout, lines);
      const res = await axios.post(`${API}/events/${eventId}/submit-expense`, fd);
      setSubmitted({ count: res.data.count, total: lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0) });
      toast.success(res.data.message || "Expense submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setLoading(false); }
  };

  const eventName = event?.name || "";
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-white to-slate-100">
        <div className="text-center max-w-sm" data-testid="expense-submitted">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">Expense Submitted</h2>
          <p className="text-slate-500 text-sm">
            {submitted.count} line{submitted.count === 1 ? "" : "s"} totalling <strong>&euro;{submitted.total.toFixed(2)}</strong> submitted for <strong>{eventName}</strong>. A summary has been emailed to you, and you&apos;ll hear from us once it&apos;s reviewed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gradient-to-b from-white to-slate-100">
      <Toaster position="top-center" richColors />
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <img src="/mymaitree-logo.png" alt="My Maitree" className="w-24 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-[#1e3a5f]">Expense Submission</h1>
          {eventName && <p className="text-slate-600 font-medium">{eventName}</p>}
          {event?.start_date && <p className="text-slate-400 text-xs">{event.start_date} - {event.end_date || ""}</p>}
        </div>
        {event && event.expenses_open === false ? (
          <div className="bg-white rounded-2xl p-6 shadow-sm text-center" data-testid="expense-closed">
            <Lock className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <p className="font-semibold text-[#1e3a5f]">Expense submissions are closed</p>
            <p className="text-sm text-slate-500 mt-1">This event is no longer accepting expense claims. Please contact the finance team if you have questions.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-5 shadow-sm space-y-4" data-testid="expense-form">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Your Name *</label>
              <input value={submitter.name} onChange={e => setSubmitter({ ...submitter, name: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border text-sm" required data-testid="exp-name" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Email *</label>
              <input type="email" value={submitter.email} onChange={e => setSubmitter({ ...submitter, email: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border text-sm" required data-testid="exp-email" />
            </div>
            <PayoutPicker payout={payout} setPayout={setPayout} />
            <ExpenseLines lines={lines} setLines={setLines} categories={categories} />
            <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-[#1e3a5f] text-white font-bold disabled:opacity-50" data-testid="expense-submit">
              {loading ? "Submitting..." : `Submit ${lines.length} line${lines.length === 1 ? "" : "s"}`}
            </button>
          </form>
        )}
        <p className="text-center text-xs text-slate-400 mt-4">Maitree e.V. - my.maitree-ev.org</p>
      </div>
    </div>
  );
}
